import { create } from "zustand";

// ─── Types ──────────────────────────────────────────────

export type DebugLogLevel = "info" | "warn" | "error";

export interface DebugLogEntry {
  timestamp: string;
  level: DebugLogLevel;
  category: string;
  message: string;
  meta?: Record<string, unknown>;
}

// ─── Constants ──────────────────────────────────────────

const STORAGE_KEY = "wexare_debug_mode";
const MAX_LOG_ENTRIES = 200;

// ─── Persisted state ────────────────────────────────────

function getStoredEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// ─── Store ──────────────────────────────────────────────

interface DebugStore {
  enabled: boolean;
  logs: DebugLogEntry[];
  toggle: () => void;
  addLog: (entry: Omit<DebugLogEntry, "timestamp">) => void;
  clearLogs: () => void;
}

export const useDebugStore = create<DebugStore>((set) => ({
  enabled: getStoredEnabled(),
  logs: [],

  toggle: () =>
    set((s) => {
      const next = !s.enabled;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* restricted storage */
      }
      return { enabled: next };
    }),

  addLog: (entry) =>
    set((s) => {
      const log: DebugLogEntry = {
        ...entry,
        timestamp: new Date().toISOString(),
      };
      const next = [...s.logs, log];
      // Ring buffer: keep only the most recent entries
      if (next.length > MAX_LOG_ENTRIES) {
        return { logs: next.slice(next.length - MAX_LOG_ENTRIES) };
      }
      return { logs: next };
    }),

  clearLogs: () => set({ logs: [] }),
}));
